import { formatCurrency, formatMoney, formatMoneyInput } from "@utils";
import CustomInputSelect from "../CustomInputSelect/CustomInputSelect";
import { useRef, useState, useEffect } from "react";
import InputCustom from "../InputCustom";

interface DynamicContentSimuiladorProps {
    selectedFuelOption: string | number;
    kmInputValue: number;
    priceInputValue: string;
    fuelType: { value: number; label: string }[];
    tollValue: number;
    fuelValue: number;
    totalValue: number;
    showResults: boolean;
    priceInputRef: React.RefObject<HTMLInputElement>;
    priceOperational: string;
    handleFuelOptionChange: (newValue: string | number) => void;
    handleKmInputChange: (e: number) => void;
    handlePriceInputChange: (e: string) => void;
    handlePriceOperational: (value: string) => void;
    fuelOptionError?: boolean;
    fuelSelectRef?: React.RefObject<HTMLSelectElement>;
    errorPrice: boolean;
    errorKm: boolean;
    isTrip: boolean;
}

const DynamicContentSimuilador = ({
    selectedFuelOption,
    kmInputValue,
    priceInputValue,
    fuelType,
    tollValue,
    fuelValue,
    totalValue,
    showResults,
    priceInputRef,
    priceOperational,
    handleFuelOptionChange,
    handleKmInputChange,
    handlePriceInputChange,
    handlePriceOperational,
    fuelSelectRef,
    fuelOptionError = false,
    errorKm = false,
    errorPrice = false,
    isTrip = false,
}: DynamicContentSimuiladorProps) => {

    const [localKmValue, setLocalKmValue] = useState<string>("0");

    const [localPriceValue, setLocalPriceValue] = useState<string>("0,00");
    
    const [localOperationalValue, setLocalOperationalValue] = useState<string>("0,00");

    const FIELD_LIMITS = {
        consumo: 5,
        preco: 6,
        operacional: 6
    };
    
    const formatConsumoInput = (value: string, maxDigits: number = 5): void => {

        let numeric = value.replace(/[^\d.,]/g, "").replace(",", ".");

        const digitsOnly = numeric.replace(/\./g, "");

        if (digitsOnly.length <= maxDigits) {

            if (numeric === "") {
                setLocalKmValue("0");
                handleKmInputChange(0);
            } else {

                setLocalKmValue(numeric);

                const numericValue = parseFloat(numeric) || 0;
                handleKmInputChange(numericValue);
            }
        }
    };

    useEffect(() => {
        if (kmInputValue) {
            setLocalKmValue(kmInputValue.toString());
        } else if (kmInputValue === 0) {
            setLocalKmValue("0");
        }
    }, [kmInputValue]);

    useEffect(() => {
        if (priceInputValue && priceInputValue !== "0,00") {
            setLocalPriceValue(priceInputValue);
        } else if (!priceInputValue || priceInputValue === "0,00") {
            setLocalPriceValue("0,00");
        }
    }, [priceInputValue]);
    
    useEffect(() => {
        if (priceOperational && priceOperational !== "0,00") {
            setLocalOperationalValue(priceOperational);
        } else if (!priceOperational || priceOperational === "0,00") {
            setLocalOperationalValue("0,00");
        }
    }, [priceOperational]);
    
    const mountValue = (value: string) => {
        return "R$ " + value;
    };


    const RowCalc = ({
        label,
        value,
        showBorder = true,
        bold,
    }: {
        label: string;
        value: string;
        showBorder?: boolean;
        bold?: boolean;
    }) => {
        return (
            <div
                className={`
                display-flex align-items-center 
                justify-content-between height-40 
                ${showBorder ? "border border-bottom-width-1 border-bottom-only" : ""}
            `}
            >
                <label className={`text-normal text-size-14 ${bold ? "text-bold text-size-16 text-color-black" : ""}`}>
                    {label}
                </label>
                <label className={`text-normal text-size-14 ${bold ? "text-bold text-size-16 text-color-black" : ""}`}>
                    {value}
                </label>
            </div>
        );
    };


    return (
        <div className="padding-x-15 display-flex flex-column gap-20">
            {isTrip ? (
                <div className="display-flex gap-5">
                    <span className="rioglyph rioglyph-info-sign margin-top-3 text-color-info"></span>
                    <span className="text-normal text-size-14">Para receber uma sugestão de <b>rota inteligente</b>, preencha os campos <b>Consumo do veículo (km/litro)</b> e <b>Preço do combustível (R$/litro)</b>. Essas informações são essenciais para calcular a melhor sugestão para você.</span>
                </div>
            ): null}
            
            <div className="width-50pct">
                <label>Tipo de Alimentação</label>
                <CustomInputSelect
                    options={fuelType}
                    value={selectedFuelOption === 0 ? "" : selectedFuelOption}
                    onChange={handleFuelOptionChange}
                    className={fuelOptionError ? "simulador-input-error" : ""}
                    selectRef={fuelSelectRef}
                />
            </div>

            <div className="display-flex gap-10">
                <InputCustom
                    onChange={(e) => formatConsumoInput(e, FIELD_LIMITS.consumo)}
                    value={localKmValue}
                    label={`Consumo veículo (${selectedFuelOption === "8" ? "km/kwh" : "km/litro"})`}
                    right={selectedFuelOption === "8" ? "km/kwh" : "km/litro"}
                    error={errorKm}
                    placeholder="0"
                    id="1"
                />


                <InputCustom
                    label={`Preço combustível (${selectedFuelOption === "8" ? "R$/kw" : "R$/litro"})`}
                    value={mountValue(localPriceValue)}
                    error={errorPrice}
                    onChange={(e) => {
                        const formattedValue = formatMoneyInput(e, FIELD_LIMITS.preco);
                        setLocalPriceValue(formattedValue);
                        handlePriceInputChange(formattedValue);
                    }}
                    placeholder="R$ 0,00"
                    id="2"
                />

                <InputCustom
                    label="Custo operacional (R$)"
                    value={mountValue(localOperationalValue)}
                    onChange={(e) => {
                        const formattedValue = formatMoneyInput(e, FIELD_LIMITS.operacional);
                        setLocalOperationalValue(formattedValue);
                        handlePriceOperational(formattedValue);
                    }}
                    placeholder="R$ 0,00"
                    id="3"
                />
            </div>

            {showResults && (
                <div className="display-flex flex-column">
                    <RowCalc label="Custo combustível" value={formatCurrency(fuelValue)} />
                    <RowCalc label="Custo pedágio" value={formatCurrency(tollValue)} />
                    <RowCalc label="Custo operacional" value={"R$ " + localOperationalValue} />
                    <RowCalc label="TOTAL" value={formatCurrency(totalValue)} showBorder={false} bold />
                </div>
            )}
        </div>
    );
};

export default DynamicContentSimuilador;

